import { useState, useEffect, useContext } from 'react'
import styles from './styles.module.css'
import { Input, Select, Checkbox } from '../../../components/ModalInputs'
import Button from '../../../components/Button'
import Table from '../../../components/Table'
import { StaffRosterContext } from '../../../utils/context'
import { useSelector } from 'react-redux'

// Данные для дропдаунов
const educationLevels = [
  { name: '', value: '' },
  { name: 'Среднее общее', value: 'secondary' },
  { name: 'Среднее профессиональное', value: 'secondary_prof' },
  { name: 'Высшее - бакалавриат', value: 'bachelor' },
  { name: 'Высшее - специалитет', value: 'specialist' },
  { name: 'Высшее - магистратура', value: 'master' },
  { name: 'Аспирантура', value: 'postgraduate' },
  { name: 'Докторантура', value: 'doctoral' }
]

const postgraduateEducation = [
  { name: '', value: '' },
  { name: 'Аспирантура', value: 'postgraduate' },
  { name: 'Адъюнктура', value: 'adjunct' },
  { name: 'Ординатура', value: 'residency' },
  { name: 'Интернатура', value: 'internship' },
  { name: 'Докторантура', value: 'doctoral' }
]

const documentNames = [
  { name: '', value: '' },
  { name: 'Диплом о высшем образовании', value: 'diploma_higher' },
  { name: 'Диплом о среднем профессиональном образовании', value: 'diploma_secondary' },
  { name: 'Аттестат о среднем общем образовании', value: 'certificate_secondary' },
  { name: 'Диплом кандидата наук', value: 'diploma_candidate' },
  { name: 'Диплом доктора наук', value: 'diploma_doctor' },
  { name: 'Удостоверение', value: 'certificate' }
]

const institutions = [
  { name: '', value: '' },
  { name: 'МГУ им. М.В. Ломоносова', value: '1' },
  { name: 'СПбГУ', value: '2' },
  { name: 'МГПУ', value: '3' },
  { name: 'РГПУ им. А.И. Герцена', value: '4' },
  { name: 'НИУ ВШЭ', value: '5' },
  { name: 'МГИМО', value: '6' },
  { name: 'ИТМО', value: '7' }
]

const specialties = [
  { name: '', value: '' },
  { name: 'Педагогическое образование', value: 'pedagogy' },
  { name: 'Математика', value: 'math' },
  { name: 'Физика', value: 'physics' },
  { name: 'Русский язык и литература', value: 'russian' },
  { name: 'Иностранный язык', value: 'foreign' },
  { name: 'История', value: 'history' },
  { name: 'Информатика', value: 'informatics' },
  { name: 'Биология', value: 'biology' },
  { name: 'Химия', value: 'chemistry' }
]

const API_TAB = 'education'

export default function Education() {
  const { allForms, trackDeletion } = useContext(StaffRosterContext)
  const { values, setFieldValue } = allForms

  // Данные из API (Redux)
  const { personTabData } = useSelector(state => state.personnel)
  const { education: apiEducation } = personTabData || {}

  const [formData, setFormData] = useState({
    isMain: false,
    education: '',
    postgraduate: '',
    institution: '',
    documentName: '',
    series: '',
    number: '',
    issueDate: '',
    endDate: '',
    oksoCode: '',
    specialtyName: '',
    okpdrCode: '',
    professionName: ''
  })

  const [selectedRow, setSelectedRow] = useState(null)

  // Данные таблицы берем из Formik
  const tableData = values.education || []

  // Синхронизация API данных с Formik (только при первой загрузке)
  useEffect(() => {
    if (apiEducation && !tableData.length) {
      setFieldValue('education', apiEducation)
    }
  }, [apiEducation, tableData.length, setFieldValue])

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }))
  }

  const handleRowSelect = (row) => {
    const id = row.xp_key || row.id
    setSelectedRow(selectedRow === id ? null : id)
  }

  const handleAddRow = () => {
    const newRow = {
      id: `new_${Date.now()}`, // Временный ID для новых записей
      isMain: formData.isMain,
      education: formData.education,
      institution: formData.institution,
      documentName: formData.documentName,
      series: formData.series,
      number: formData.number,
      issueDate: formData.issueDate,
      endDate: formData.endDate,
      oksoCode: formData.oksoCode,
      specialtyName: formData.specialtyName,
      okpdrCode: formData.okpdrCode,
      professionName: formData.professionName
    }
    // Сохраняем в Formik
    setFieldValue('education', [...tableData, newRow])

    // Сброс формы
    setFormData({
      isMain: false,
      education: '',
      postgraduate: '',
      institution: '',
      documentName: '',
      series: '',
      number: '',
      issueDate: '',
      endDate: '',
      oksoCode: '',
      specialtyName: '',
      okpdrCode: '',
      professionName: ''
    })
  }

  const handleDeleteRow = () => {
    if (selectedRow !== null) {
      const rowToDelete = tableData.find(row => (row.xp_key || row.id) === selectedRow)

      if (rowToDelete) {
        // Если это существующая запись (есть xp_key), трекаем удаление
        const pk = rowToDelete.xp_key || rowToDelete.id || rowToDelete.xp_fm_id
        if (pk && !String(pk).startsWith('new_')) {
          trackDeletion(API_TAB, pk)
        }

        // Обновляем в Formik
        const updatedData = tableData.filter(row => (row.xp_key || row.id) !== selectedRow)
        setFieldValue('education', updatedData)
        setSelectedRow(null)
      }
    }
  }

  const renderOptions = ({ name, value }) => (
    <option key={value || 'empty'} value={value}>{name}</option>
  )

  const getDisplayValue = (row, field, options) => {
    if (options) {
      const found = options.find(o => o.value === row[field])
      return found?.name || row[field] || ''
    }
    return row[field] || ''
  }

  return (
    <div className={styles.tabWrapper}>
      {/* Форма */}
      <div className={styles.qualificationForm}>
        {/* Строка 1 */}
        <div className={styles.inputsRow}>
          <Checkbox
            label="Основное"
            name="isMain"
            checked={formData.isMain}
            onChange={handleFormChange}
          />
        </div>

        {/* Строка 2 */}
        <div className={styles.inputsRow}>
          <Select
            label="Образование"
            name="education"
            value={formData.education}
            onChange={handleFormChange}
          >
            {educationLevels.map(renderOptions)}
          </Select>
          <Select
            label="Послевузовское образование"
            name="postgraduate"
            value={formData.postgraduate}
            onChange={handleFormChange}
          >
            {postgraduateEducation.map(renderOptions)}
          </Select>
        </div>

        {/* Строка 3 */}
        <div className={styles.inputsRow}>
          <Select
            label="Наименование ОУ"
            name="institution"
            value={formData.institution}
            onChange={handleFormChange}
          >
            {institutions.map(renderOptions)}
          </Select>
          <Select
            label="Название документа"
            name="documentName"
            value={formData.documentName}
            onChange={handleFormChange}
          >
            {documentNames.map(renderOptions)}
          </Select>
        </div>

        {/* Строка 4 */}
        <div className={styles.inputsRow}>
          <Input
            label="Серия"
            name="series"
            value={formData.series}
            onChange={handleFormChange}
          />
          <Input
            label="Номер"
            name="number"
            value={formData.number}
            onChange={handleFormChange}
          />
        </div>

        {/* Строка 5 */}
        <div className={styles.inputsRow}>
          <Input
            label="Дата выдачи"
            type="date"
            name="issueDate"
            value={formData.issueDate}
            onChange={handleFormChange}
          />
          <Input
            label="Дата окончания"
            type="date"
            name="endDate"
            value={formData.endDate}
            onChange={handleFormChange}
          />
        </div>

        {/* Строка 6 */}
        <div className={styles.inputsRow}>
          <Input
            label="Код ОКСО специальности"
            name="oksoCode"
            value={formData.oksoCode}
            onChange={handleFormChange}
          />
          <Select
            label="Название специальности"
            name="specialtyName"
            value={formData.specialtyName}
            onChange={handleFormChange}
          >
            {specialties.map(renderOptions)}
          </Select>
        </div>

        {/* Строка 7 */}
        <div className={styles.inputsRow}>
          <Input
            label="Код ОКПДР профессии"
            name="okpdrCode"
            value={formData.okpdrCode}
            onChange={handleFormChange}
          />
          <Input
            label="Название профессии"
            name="professionName"
            value={formData.professionName}
            onChange={handleFormChange}
          />
        </div>

        {/* Кнопки */}
        <div className={styles.inputsRow}>
          <div className={styles.qualificationButtonsInline}>
            <Button onClick={handleAddRow}>+ Добавить строку</Button>
            <Button mode="delete" onClick={handleDeleteRow}>× Удалить строку</Button>
          </div>
        </div>
      </div>

      {/* Таблица */}
      <div className={styles.qualificationTableSection}>
        <Table>
          <thead>
            <tr>
              <th>Осн.</th>
              <th>Образование</th>
              <th>Наименование ОУ</th>
              <th>Документ</th>
              <th>Серия</th>
              <th>Номер</th>
              <th>Дата выдачи</th>
              <th>Специальность</th>
            </tr>
          </thead>
          <tbody>
            {tableData.length > 0 ? (
              tableData.map(row => {
                const rowId = row.xp_key || row.id
                return (
                  <tr
                    key={rowId}
                    onClick={() => handleRowSelect(row)}
                    className={selectedRow === rowId ? styles.selectedRow : ''}
                  >
                    <td>{row.isMain ? 'Да' : 'Нет'}</td>
                    <td>{getDisplayValue(row, 'education', educationLevels)}</td>
                    <td>{getDisplayValue(row, 'institution', institutions)}</td>
                    <td>{getDisplayValue(row, 'documentName', documentNames)}</td>
                    <td>{row.series || ''}</td>
                    <td>{row.number || ''}</td>
                    <td>{row.issueDate || ''}</td>
                    <td>{getDisplayValue(row, 'specialtyName', specialties)}</td>
                  </tr>
                )
              })
            ) : (
              <tr>
                <td colSpan="8" className={styles.emptyTable}>
                  Нет данных об образовании
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  )
}
